import React from "react";
import { Link } from "react-router-dom";

import { xboxOnboardSiteLocationPrefix } from "../../constants/urls";
import { useStrings } from "../../localization/localizationHelpers";
import { CopyButton } from "./CopyButton";
import * as styles from "./SupportAssistant.module.css";

const hubPathPattern = new RegExp(`^${xboxOnboardSiteLocationPrefix}(?:[/?#]|$)`);

const isHubPath = (url: string): boolean => hubPathPattern.test(url);

/** Only relative hub paths and absolute https: URLs are clickable; anything else stays text. */
export const isAllowedAssistantLink = (url: string): boolean => {
  try {
    return isHubPath(url) || new URL(url).protocol === "https:";
  } catch {
    return false;
  }
};

type AssistantLinkProps = Readonly<{ href: string; className?: string; children: React.ReactNode }>;

/** Hub links stay in the SPA so the in-memory conversation survives; others open in a new tab. */
export const AssistantLink: React.FC<AssistantLinkProps> = ({ href, className, children }) =>
  isHubPath(href) ? (
    <Link to={href} className={className}>
      {children}
    </Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );

const inlinePattern =
  /`([^`]+)`|\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|\*([^*\s][^*]*?)\*|_([^_\s][^_]*?)_/g;

const renderInline = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(inlinePattern)) {
    const [whole, code, bold, linkText, linkUrl, italic, underscoreItalic] = match;
    const index = match.index ?? 0;
    if (index > last) {
      nodes.push(text.slice(last, index));
    }
    const key = nodes.length;
    if (code !== undefined) {
      nodes.push(<code key={key}>{code}</code>);
    } else if (bold !== undefined) {
      nodes.push(<strong key={key}>{renderInline(bold)}</strong>);
    } else if (linkText !== undefined) {
      nodes.push(
        isAllowedAssistantLink(linkUrl) ? (
          <AssistantLink key={key} href={linkUrl} className={styles.inlineLink}>
            {linkText}
          </AssistantLink>
        ) : (
          linkText
        )
      );
    } else {
      nodes.push(<em key={key}>{renderInline(italic ?? underscoreItalic)}</em>);
    }
    last = index + whole.length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
};

type CodeBlockProps = Readonly<{ code: string; language: string }>;

const CodeBlock: React.FC<CodeBlockProps> = ({ code, language }) => {
  const [copyCodeLabel] = useStrings("supportAssistant_copyCode");
  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeHeader}>
        <span>{language}</span>
        <CopyButton label={copyCodeLabel} text={code} />
      </div>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const headingPattern = /^(#{1,3})\s+(.*)$/;
const unorderedPattern = /^\s*[-*+]\s+(.*)$/;
const orderedPattern = /^\s*\d+[.)]\s+(.*)$/;
const fencePattern = /^\s*```/;
const blockPatterns = [fencePattern, headingPattern, unorderedPattern, orderedPattern];

const splitTableRow = (line: string): string[] => {
  const text = line.trim();
  const cells = [""];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\\" && i + 1 < text.length) {
      cells[cells.length - 1] += text.slice(i, i + 2);
      i++;
    } else if (text[i] === "|") {
      cells.push("");
    } else {
      cells[cells.length - 1] += text[i];
    }
  }
  if (text.startsWith("|")) {
    cells.shift();
  }
  if (cells[cells.length - 1] === "") {
    cells.pop();
  }
  return cells.map(cell => cell.trim().replace(/\\\|/g, "|"));
};

/**
 * Renders the assistant's markdown subset as React elements: paragraphs, `#`-`###` headings,
 * lists, pipe tables, fenced code, inline code, bold, italic, and allow-listed links. Raw HTML stays text.
 */
export const renderAssistantMarkdown = (markdown: string): React.ReactNode[] => {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push(<p key={blocks.length}>{renderInline(paragraph.join(" "))}</p>);
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (fencePattern.test(line)) {
      flushParagraph();
      const language = line.trim().slice(3).trim();
      const code: string[] = [];
      for (i++; i < lines.length && !fencePattern.test(lines[i]); i++) {
        code.push(lines[i]);
      }
      blocks.push(<CodeBlock key={blocks.length} code={code.join("\n")} language={language} />);
      continue;
    }
    if (line.includes("|") && i + 1 < lines.length) {
      const headers = splitTableRow(line);
      const delimiters = splitTableRow(lines[i + 1]);
      if (
        headers.length > 0 &&
        headers.length === delimiters.length &&
        delimiters.every(cell => /^:?-+:?$/.test(cell))
      ) {
        flushParagraph();
        const alignments = delimiters.map(cell =>
          cell.endsWith(":")
            ? cell.startsWith(":")
              ? styles.tableCenter
              : styles.tableRight
            : undefined
        );
        const rows: string[][] = [];
        for (
          i += 2;
          i < lines.length &&
          lines[i].trim() &&
          lines[i].includes("|") &&
          !blockPatterns.some(pattern => pattern.test(lines[i]));
          i++
        ) {
          rows.push(splitTableRow(lines[i]));
        }
        i--;
        /* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Scrollable table regions need keyboard focus. */
        blocks.push(
          <div
            key={blocks.length}
            className={styles.tableBlock}
            role="region"
            aria-label={headers.join(", ")}
            tabIndex={0}
          >
            <table>
              <thead>
                <tr>
                  {headers.map((header, column) => (
                    <th key={column} scope="col" className={alignments[column]}>
                      {renderInline(header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    {headers.map((_, column) => (
                      <td key={column} className={alignments[column]}>
                        {renderInline(row[column] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
        continue;
      }
    }
    const heading = headingPattern.exec(line);
    if (heading) {
      flushParagraph();
      const Heading = (["h3", "h4", "h5"] as const)[heading[1].length - 1];
      blocks.push(<Heading key={blocks.length}>{renderInline(heading[2])}</Heading>);
      continue;
    }
    const listPattern = unorderedPattern.test(line)
      ? unorderedPattern
      : orderedPattern.test(line)
      ? orderedPattern
      : undefined;
    if (listPattern) {
      flushParagraph();
      const items: string[] = [];
      for (; i < lines.length && listPattern.test(lines[i]); i++) {
        items.push(listPattern.exec(lines[i])?.[1] ?? "");
      }
      i--;
      const List = listPattern === orderedPattern ? "ol" : "ul";
      blocks.push(
        <List key={blocks.length}>
          {items.map((item, index) => (
            <li key={index}>{renderInline(item)}</li>
          ))}
        </List>
      );
      continue;
    }
    if (line.trim()) {
      paragraph.push(line.trim());
    } else {
      flushParagraph();
    }
  }
  flushParagraph();
  return blocks;
};
